import { Fragment, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { formatUsd } from '@/components/gcp/gcpHelpers'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import { FixedAssetsSortableHeader } from '@/pages/fixed-assets/FixedAssetsSortableHeader'
import {
  filterRowsWithTree,
  toggleSingleColumnSort,
} from '@/pages/fixed-assets/fixedAssetsTableFilters'
import PurchasingPriceSparkline from '@/pages/purchasing/PurchasingPriceSparkline'
import {
  fetchAllPurchasingProducts,
  fetchAllPurchasingPurchaseLines,
  fetchAllPurchasingVendorItems,
} from '@/pages/purchasing/purchasingFetchAll'
import {
  BRAND_POLICY_FILTER_OPTIONS,
  buildPurchasingProductsExportMatrix,
  buildPurchasingProductsFilterFields,
  fixedColumnStyle,
  isPurchasingProductsColumnSortable,
  migrateLegacyPurchasingProductsColumnWidths,
  PURCHASING_PRODUCTS_COLUMN_KEYS,
  PURCHASING_PRODUCTS_COLUMN_LABELS,
  PURCHASING_PRODUCTS_DEFAULT_SORT,
  PURCHASING_PRODUCTS_DEFAULT_WIDTHS,
  PURCHASING_PRODUCTS_FIELD_DEFINITIONS,
  PURCHASING_PRODUCTS_GROUP_OPTIONS,
  PURCHASING_PRODUCTS_PAGE_KEY,
  type PurchasingProductsColumnKey,
} from '@/pages/purchasing/purchasingProductsTableConfig'
import {
  exportPurchasingTableCsv,
  exportPurchasingTableToGoogleSheet,
} from '@/pages/purchasing/purchasingTableExport'
import {
  formatQboVendorLabel,
  useQboVendorDisplayNameMap,
} from '@/pages/purchasing/purchasingVendorNames'
import {
  purchasingBrandPolicyBadge,
  purchasingProductStatusBadge,
  renderPurchasingStatusBadge,
} from '@/pages/purchasing/purchasingStatusBadges'
import {
  searchPurchasingMeezIngredients,
  updatePurchasingProduct,
  type PurchasingProductRow,
} from '@/services/purchasing'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  FilterBuilderDrawer,
  GroupBySelect,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'

function distinctOptions(values: string[]) {
  return [...new Set(values.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }))
}

function groupRows<T>(
  rows: T[],
  groupBy: string,
  getLabel: (row: T) => string,
): Array<{ label: string; items: T[] }> {
  if (!groupBy) return [{ label: '', items: rows }]
  const groups = new Map<string, T[]>()
  for (const row of rows) {
    const label = getLabel(row) || '—'
    const bucket = groups.get(label)
    if (bucket) bucket.push(row)
    else groups.set(label, [row])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, items]) => ({ label, items }))
}

export default function PurchasingProducts() {
  const { canEdit } = usePageAccess('purchasing')
  const [rows, setRows] = useState<PurchasingProductRow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQ, setSearchQ] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(PURCHASING_PRODUCTS_DEFAULT_SORT)
  const [groupBy, setGroupBy] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [detailVendorItems, setDetailVendorItems] = useState<
    Awaited<ReturnType<typeof fetchAllPurchasingVendorItems>>
  >([])
  const [detailLines, setDetailLines] = useState<
    Awaited<ReturnType<typeof fetchAllPurchasingPurchaseLines>>
  >([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [editName, setEditName] = useState('')
  const [editFamily, setEditFamily] = useState('')
  const [editBrandPolicy, setEditBrandPolicy] = useState('agnostic')
  const [editMeezId, setEditMeezId] = useState<string | null>(null)
  const [meezOptions, setMeezOptions] = useState<SelectOption[]>([])
  const [savingProduct, setSavingProduct] = useState(false)

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(PURCHASING_PRODUCTS_PAGE_KEY, PURCHASING_PRODUCTS_FIELD_DEFINITIONS),
  )

  useEffect(() => {
    migrateLegacyPurchasingProductsColumnWidths()
  }, [])

  const { getWidth, startResize } = useColumnWidths({
    pageKey: PURCHASING_PRODUCTS_PAGE_KEY,
    columnKeys: [...PURCHASING_PRODUCTS_COLUMN_KEYS],
    defaults: PURCHASING_PRODUCTS_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const vendorNameMap = useQboVendorDisplayNameMap()

  const filterFields = useMemo(
    () =>
      buildPurchasingProductsFilterFields(
        distinctOptions(rows.map((row) => row.family ?? '')),
        distinctOptions(rows.map((row) => row.atomic_uom)),
        distinctOptions(rows.map((row) => row.cogs_account_id)),
      ),
    [rows],
  )

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true)
      const trimmedQ = searchQ.trim()
      const data = trimmedQ
        ? await fetchAllPurchasingProducts({ q: trimmedQ })
        : await fetchAllPurchasingProducts()
      setRows(data)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load products'))
    } finally {
      setLoading(false)
    }
  }, [searchQ])

  useEffect(() => {
    void loadProducts()
  }, [loadProducts])

  const treeFiltered = useMemo(
    () => filterRowsWithTree(rows, filterTree, filterFields),
    [rows, filterTree, filterFields],
  )

  const sortedRows = useMemo(
    () => applySorts(treeFiltered, sortSpecs, PURCHASING_PRODUCTS_FIELD_DEFINITIONS),
    [treeFiltered, sortSpecs],
  )

  const selectedProduct = useMemo(
    () => rows.find((row) => row.id === selectedProductId) ?? null,
    [rows, selectedProductId],
  )

  const visibleColumnKeys = useMemo(
    () => getVisibleFieldKeys(PURCHASING_PRODUCTS_FIELD_DEFINITIONS, columnVisibility),
    [columnVisibility],
  )

  const visibleTableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys],
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: visibleTableWidth,
      minWidth: visibleTableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, visibleTableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return {
        ...fixedColumnStyle(getWidth(columnKey)),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }
    },
    [allowColumnResize, getWidth],
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return fixedColumnStyle(getWidth(columnKey))
    },
    [allowColumnResize, getWidth],
  )

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )
  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])
  const showingSubset = activeFilterCount > 0 || searchQ.trim().length > 0

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const groupedSections = useMemo(() => {
    return groupRows(sortedRows, groupBy, (row) => {
      if (groupBy === 'family') return row.family ?? '—'
      if (groupBy === 'atomic_uom') return row.atomic_uom
      if (groupBy === 'brand_policy') return row.brand_policy
      if (groupBy === 'status') return row.status
      return ''
    })
  }, [sortedRows, groupBy])

  useEffect(() => {
    if (!selectedProductId) return
    const product = rows.find((row) => row.id === selectedProductId)
    if (!product) return
    setEditName(product.name)
    setEditFamily(product.family ?? '')
    setEditBrandPolicy(product.brand_policy)
    setEditMeezId(product.meez_ingredient_id)
    void searchMeezIngredients(product.name).then((options) => {
      const seed = product.meez_ingredient_id
        ? [
            {
              id: product.meez_ingredient_id,
              label: product.meez_ingredient_name ?? product.meez_ingredient_id,
            },
          ]
        : []
      const merged = [...seed]
      for (const opt of options) {
        if (!merged.some((entry) => entry.id === opt.id)) merged.push(opt)
      }
      setMeezOptions(merged)
    })
    setDetailLoading(true)
    void (async () => {
      try {
        const [vendorItems, lines] = await Promise.all([
          fetchAllPurchasingVendorItems({ product: selectedProductId }),
          fetchAllPurchasingPurchaseLines({ product_id: selectedProductId }),
        ])
        setDetailVendorItems(vendorItems)
        setDetailLines(lines)
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load product detail'))
      } finally {
        setDetailLoading(false)
      }
    })()
  }, [selectedProductId, rows])

  async function handleSaveProduct() {
    if (!selectedProduct || !canEdit) return
    setSavingProduct(true)
    try {
      const updated = await updatePurchasingProduct(selectedProduct.id, {
        name: editName.trim(),
        family: editFamily.trim() || undefined,
        brand_policy: editBrandPolicy,
        meez_ingredient_id: editMeezId,
      })
      setRows((prev) =>
        prev.map((row) =>
          row.id === updated.id
            ? {
                ...row,
                name: updated.name,
                family: updated.family,
                brand_policy: updated.brand_policy,
                meez_ingredient_id: updated.meez_ingredient_id,
                meez_ingredient_name: updated.meez_ingredient_name,
              }
            : row,
        ),
      )
      toast.success('Product updated')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update product'))
    } finally {
      setSavingProduct(false)
    }
  }

  function exportCsv() {
    const { headers, rows: exportRows } = buildPurchasingProductsExportMatrix(sortedRows)
    exportPurchasingTableCsv('purchasing-products.csv', headers, exportRows)
  }

  async function exportGoogleSheet() {
    if (sortedRows.length === 0 || isExporting) return
    setIsExporting(true)
    try {
      const { headers, rows: exportRows } = buildPurchasingProductsExportMatrix(sortedRows)
      const result = await exportPurchasingTableToGoogleSheet(
        'Purchasing products',
        headers,
        exportRows,
      )
      toast.success(`Created Google Sheet: ${result.name}`)
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to export products to Google Sheet'))
    } finally {
      setIsExporting(false)
    }
  }

  function renderCell(columnKey: PurchasingProductsColumnKey, row: PurchasingProductRow) {
    switch (columnKey) {
      case 'last_cost_per_atomic_unit':
        return row.last_cost_per_atomic_unit
          ? formatUsd(Number(row.last_cost_per_atomic_unit))
          : '—'
      case 'brand_policy': {
        const badge = purchasingBrandPolicyBadge(row.brand_policy)
        return <span className={renderPurchasingStatusBadge(badge)}>{badge.label}</span>
      }
      case 'status': {
        const badge = purchasingProductStatusBadge(row.status)
        return <span className={renderPurchasingStatusBadge(badge)}>{badge.label}</span>
      }
      case 'meez_ingredient':
        return row.meez_ingredient_name ?? row.meez_ingredient_id ?? '—'
      default: {
        const value = row[columnKey as keyof PurchasingProductRow]
        return value == null || value === '' ? '—' : String(value)
      }
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TablePageShell
        className="min-h-0 flex-1"
        rowCount={
          <div className="mb-2 text-xs text-gray-500 sm:text-sm">
            <span className="font-medium text-gray-700">
              {sortedRows.length.toLocaleString()} product{sortedRows.length !== 1 ? 's' : ''}
            </span>
            {showingSubset ? (
              <span className="ml-1">(of {rows.length.toLocaleString()} loaded)</span>
            ) : null}
          </div>
        }
        toolbarLeft={
          <SearchInput
            value={searchQ}
            onChange={setSearchQ}
            placeholder="Search products…"
            className="max-w-md flex-1"
          />
        }
        toolbarRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void loadProducts()}
              disabled={loading}
              aria-label="Refresh products"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <TableToolbar
              groupBySlot={
                <GroupBySelect
                  value={groupBy}
                  onChange={setGroupBy}
                  options={PURCHASING_PRODUCTS_GROUP_OPTIONS}
                  aria-label="Group products by"
                />
              }
              filterCount={activeFilterCount}
              sortCount={sortSpecs.length}
              showFilters={showFilters}
              showSort={showSort}
              showColumns={showColumns}
              onToggleFilters={() => setShowFilters((open) => !open)}
              onToggleSort={() => setShowSort((open) => !open)}
              onToggleColumns={() => setShowColumns((open) => !open)}
              exportOptions={[
                {
                  id: 'sheets',
                  label: 'Google Sheet',
                  onClick: () => void exportGoogleSheet(),
                  disabled: sortedRows.length === 0,
                },
                {
                  id: 'csv',
                  label: 'CSV',
                  onClick: exportCsv,
                  disabled: sortedRows.length === 0,
                },
              ]}
              defaultExportId="sheets"
              isExporting={isExporting}
              exportDisabled={sortedRows.length === 0}
            />
          </div>
        }
        filterPills={
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={(pillId) => setFilterTree(removeFilterPill(filterTree, pillId))}
            onClearAll={() => setFilterTree(null)}
          />
        }
      >
        <div className="table-scroll-mobile h-full min-h-0 overflow-auto overscroll-x-contain">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-500">
              <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
              Loading products…
            </div>
          ) : (
            <table
              className={`table-forecast border-separate border-spacing-0${allowColumnResize ? '' : ' min-w-full'}`}
              style={tableStyle}
            >
              <thead>
                <tr>
                  {visibleColumnKeys.map((columnKey) => {
                    const sortable = isPurchasingProductsColumnSortable(columnKey)
                    if (!sortable) {
                      return (
                        <th key={columnKey} className="relative" style={headerStyle(columnKey)}>
                          <span className="inline-flex items-center pr-2">
                            {
                              PURCHASING_PRODUCTS_COLUMN_LABELS[
                                columnKey as PurchasingProductsColumnKey
                              ]
                            }
                          </span>
                        </th>
                      )
                    }
                    return (
                      <FixedAssetsSortableHeader
                        key={columnKey}
                        label={
                          PURCHASING_PRODUCTS_COLUMN_LABELS[columnKey as PurchasingProductsColumnKey]
                        }
                        sortKey={columnKey}
                        sortDirection={getSortDirection(columnKey)}
                        onSort={handleRequestSort}
                        columnKey={columnKey}
                        style={headerStyle(columnKey)}
                        onStartResize={allowColumnResize ? startResize : undefined}
                        showResize={allowColumnResize}
                      />
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumnKeys.length}
                      className="py-12 text-center text-sm text-gray-600"
                    >
                      No products match your search or filters.
                    </td>
                  </tr>
                ) : null}
                {groupedSections.map((section) => (
                  <Fragment key={section.label || 'all'}>
                    {section.label ? (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={visibleColumnKeys.length}
                          className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
                        >
                          {section.label}
                          <span className="ml-2 font-normal normal-case text-gray-400">
                            ({section.items.length})
                          </span>
                        </td>
                      </tr>
                    ) : null}
                    {section.items.map((row) => (
                      <tr
                        key={row.id}
                        className={`cursor-pointer ${selectedProductId === row.id ? 'row-cursor' : ''}`}
                        onClick={() => setSelectedProductId(row.id)}
                      >
                        {visibleColumnKeys.map((columnKey) => (
                          <td key={columnKey} style={cellStyle(columnKey)}>
                            {renderCell(columnKey as PurchasingProductsColumnKey, row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </TablePageShell>

      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />

      <SortBuilder
        open={showSort}
        fields={PURCHASING_PRODUCTS_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(PURCHASING_PRODUCTS_DEFAULT_SORT)}
      />

      <ColumnPicker
        open={showColumns}
        fields={PURCHASING_PRODUCTS_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={(visibility) => {
          setColumnVisibility(visibility)
          saveColumnVisibility(PURCHASING_PRODUCTS_PAGE_KEY, visibility)
        }}
        onClose={() => setShowColumns(false)}
        onReset={() => {
          const defaults = getDefaultColumnVisibility(PURCHASING_PRODUCTS_FIELD_DEFINITIONS)
          setColumnVisibility(defaults)
          saveColumnVisibility(PURCHASING_PRODUCTS_PAGE_KEY, defaults)
        }}
      />

      {selectedProduct ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/10"
            onClick={() => setSelectedProductId(null)}
            aria-hidden="true"
          />
          <aside
            className="fixed right-0 top-0 z-50 flex h-full w-[min(30rem,92vw)] flex-col border-l border-gray-200 bg-white shadow-xl"
            aria-label={`Product details for ${selectedProduct.name}`}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-gray-900">
                  {selectedProduct.name}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {selectedProduct.family ?? 'No family'} · {selectedProduct.atomic_uom}
                </p>
              </div>
              <Button
                variant="ghost"
                iconOnly
                aria-label="Close product detail"
                onClick={() => setSelectedProductId(null)}
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              {canEdit ? (
                <section className="mb-6 space-y-3 rounded-lg border border-gray-200 p-3">
                  <h3 className="text-sm font-semibold text-gray-900">Edit product</h3>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Name</span>
                    <input
                      className="input mt-1 w-full"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Family</span>
                    <input
                      className="input mt-1 w-full"
                      value={editFamily}
                      onChange={(e) => setEditFamily(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Brand policy</span>
                    <select
                      className="input mt-1 w-full"
                      value={editBrandPolicy}
                      onChange={(e) => setEditBrandPolicy(e.target.value)}
                    >
                      {BRAND_POLICY_FILTER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-sm">
                    <span className="font-medium text-gray-700">Meez ingredient</span>
                    <SearchableSelect
                      className="mt-1"
                      options={meezOptions}
                      value={editMeezId}
                      onChange={(id) => setEditMeezId(id)}
                      placeholder="Search Meez ingredients…"
                      allowClear
                      allowCreate
                      createLabel="Link Meez ingredient"
                      onCreateNew={async (name) => {
                        const options = await searchMeezIngredients(name)
                        setMeezOptions((prev) => {
                          const merged = [...prev]
                          for (const opt of options) {
                            if (!merged.some((entry) => entry.id === opt.id)) merged.push(opt)
                          }
                          return merged
                        })
                        return options.find((opt) =>
                          opt.label.toLowerCase() === name.trim().toLowerCase(),
                        ) ?? options[0] ?? null
                      }}
                    />
                  </label>
                  <Button
                    variant="primary"
                    disabled={savingProduct || !editName.trim()}
                    onClick={() => void handleSaveProduct()}
                  >
                    {savingProduct ? 'Saving…' : 'Save changes'}
                  </Button>
                </section>
              ) : null}

              <section className="mb-6">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">90-day price trend</h3>
                {detailLoading ? (
                  <p className="text-sm text-gray-500">Loading purchase history…</p>
                ) : (
                  <PurchasingPriceSparkline lines={detailLines} />
                )}
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-900">
                  Vendor items ({detailVendorItems.length})
                </h3>
                {detailLoading ? (
                  <p className="text-sm text-gray-500">Loading vendor items…</p>
                ) : detailVendorItems.length === 0 ? (
                  <p className="text-sm text-gray-500">No vendor items linked.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs font-medium text-gray-600">
                        <tr>
                          <th className="px-3 py-2">Vendor</th>
                          <th className="px-3 py-2">Code</th>
                          <th className="px-3 py-2">Pack</th>
                          <th className="px-3 py-2">Purchases</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailVendorItems.map((item) => (
                          <tr key={item.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">
                              {formatQboVendorLabel(item.qbo_vendor_id, vendorNameMap)}
                            </td>
                            <td className="px-3 py-2 font-medium text-gray-900">
                              {item.vendor_item_code}
                            </td>
                            <td className="px-3 py-2 text-gray-700">{item.pack_text ?? '—'}</td>
                            <td className="px-3 py-2 tabular-nums text-gray-700">
                              {item.purchase_count ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          </aside>
        </>
      ) : null}
    </div>
  )
}

async function searchMeezIngredients(query: string): Promise<SelectOption[]> {
  const trimmed = query.trim()
  if (!trimmed) return []
  const { rows } = await searchPurchasingMeezIngredients({ q: trimmed, limit: 25 })
  return rows.map((row) => ({ id: row.id, label: row.name }))
}
